/**
 * Shared reminder state for the Candidate Requests tab.
 *
 * A module-level store with subscribers (the requestsCache pattern) rather than props or context:
 * the create button lives deep inside `InboundCard` and the strip at the top of `RequestsTab`, and
 * BOTH host files sit at the 500-line ceiling — threading state through them would mean splitting
 * files for plumbing's sake. In memory only, like requestsCache: reminder titles usually name a
 * candidate, so they never touch localStorage.
 *
 * Mutations are optimistic with rollback (the dismiss-restore pattern): a reminder is a recruiter's
 * own note, so the UI answers instantly and a failed write puts the row back with a toast.
 *
 * PII: analytics events here carry booleans and day-counts ONLY — never the title, the candidate
 * name, or the thread id (the feedback-widget rule: thread ids do not reach analytics either).
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import * as api from '../../services/taRemindersApi';
import type { TaReminder } from '../../services/taRemindersApi';

interface RemindersState {
  items: TaReminder[];
  status: 'idle' | 'loading' | 'ready' | 'failed';
}

let state: RemindersState = { items: [], status: 'idle' };
const listeners = new Set<() => void>();

function setState(next: RemindersState) {
  state = next;
  listeners.forEach((l) => l());
}

const byDueAt = (items: TaReminder[]) =>
  [...items].sort((a, b) => a.dueAt.localeCompare(b.dueAt));

async function load() {
  setState({ ...state, status: 'loading' });
  try {
    setState({ items: byDueAt(await api.fetchReminders()), status: 'ready' });
  } catch {
    // The strip renders nothing on failure — reminders are an aid, not a gate on the tab.
    setState({ ...state, status: 'failed' });
  }
}

/**
 * Empty the store and tell every subscriber. Two callers: tests between cases, and AuthContext on
 * sign-out — the titles name candidates, so a shared browser must not carry them to the next
 * account, and a leftover 'ready' status would also skip the next account's load. Listeners stay
 * subscribed (mounted components must see the wipe); status 'idle' re-arms the mount-time load.
 */
export function resetRemindersStore() {
  setState({ items: [], status: 'idle' });
}

const dueInDays = (dueAt: string) =>
  Math.round((Date.parse(dueAt) - Date.now()) / 86_400_000);

export function useReminders() {
  const snapshot = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state
  );

  // First consumer triggers the load; later mounts reuse what is there. A FAILED load retries on
  // the next mount too — one transient blip used to pin the store on 'failed' for the whole
  // session, silently hiding every existing reminder behind the innocuous empty state.
  useEffect(() => {
    if (state.status === 'idle' || state.status === 'failed') void load();
  }, []);

  const create = useCallback(
    async (input: {
      title: string;
      dueAt: string;
      threadId?: string | null;
      candidateName?: string | null;
    }) => {
      const reminder = await api.createReminder(input); // caller shows validation errors
      setState({ ...state, items: byDueAt([...state.items, reminder]) });
      // Categories only — never the title, name or thread id.
      void logAnalyticsEvent('ta_reminder_created', {
        hasThread: !!input.threadId,
        dueInDays: dueInDays(reminder.dueAt),
      });
      return reminder;
    },
    []
  );

  /**
   * Roll back by re-inserting ONLY the failed row: restoring a whole pre-mutation snapshot
   * resurrected rows a concurrent operation had meanwhile removed successfully.
   */
  const restoreRow = (reminder: TaReminder) => {
    if (!state.items.some((r) => r.id === reminder.id)) {
      setState({ ...state, items: byDueAt([...state.items, reminder]) });
    }
  };

  const complete = useCallback(async (reminder: TaReminder) => {
    setState({ ...state, items: state.items.filter((r) => r.id !== reminder.id) });
    try {
      await api.updateReminder(reminder.id, { status: 'done' });
      void logAnalyticsEvent('ta_reminder_completed', {
        overdue: Date.parse(reminder.dueAt) < Date.now(),
      });
    } catch {
      restoreRow(reminder);
      showToast({ status: 'error', description: 'Could not complete the reminder — it is back.' });
    }
  }, []);

  const remove = useCallback(async (reminder: TaReminder) => {
    setState({ ...state, items: state.items.filter((r) => r.id !== reminder.id) });
    try {
      await api.deleteReminder(reminder.id);
      void logAnalyticsEvent('ta_reminder_deleted', {});
    } catch {
      restoreRow(reminder);
      showToast({ status: 'error', description: 'Could not delete the reminder — it is back.' });
    }
  }, []);

  const update = useCallback(
    async (id: string, patch: { title?: string; dueAt?: string }) => {
      const updated = await api.updateReminder(id, patch); // caller shows validation errors
      setState({
        ...state,
        items: byDueAt(state.items.map((r) => (r.id === id ? updated : r))),
      });
      return updated;
    },
    []
  );

  return {
    reminders: snapshot.items,
    loading: snapshot.status === 'loading' || snapshot.status === 'idle',
    failed: snapshot.status === 'failed',
    reload: load,
    create,
    complete,
    remove,
    update,
  };
}
